import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useField, useForm } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import WithError from '../../../components/with-error/WithError';
import useTypeOptions from '../../../hooks/useTypeOptions';
import { Input } from '../../../components/common-inputs';
import DocsButton from './components/docs-button/DocsButton';
import RepliesForm from './components/repliesForm/RepliesForm';
import DropdownWithIcons from '../../../components/common-dropdown/DropdownWithIcons';
import { ASSISTANT_TYPES, mapScoutTabsToAssistantCategories, SCOUT_TABS } from '../../../constants';
import VariationScore from './components/variation-score/VariationScore';
import AdditionalFields from './components/additional-fields/AdditionalFields';
import GenerateButton from './components/generate-button/GenerateButton';
import CommonDropdown from '../../../components/common-dropdown/CommonDropdown';
import useTargetNumberOptions from '../../../hooks/useTargetNumberOptions';
import { changeTargetNumber } from '../../../redux/scout/scoutReducer';
import Icon from '../../../icons/Icon';
import { ICONS } from '../../../icons';

import scss from './GenerationForm.module.scss';

const GenerationForm = ({ onSubmit, onReply, onResetSession, onFileUpload }) => {
  const { t } = useTranslation('scout');
  const dispatch = useDispatch();

  const [isAdditionalInputsOpen, setIsAdditionalInputsOpen] = useState(false);

  const form = useForm();
  const scoutTab = useSelector((state) => state.scout.scoutTab);
  const targetNumber = useSelector((state) => state.scout.targetNumber);
  const assistantCategory = mapScoutTabsToAssistantCategories[scoutTab];

  const targetNumberOptions = useTargetNumberOptions();

  const { input: assistantInput } = useField(`channels.${assistantCategory}.assistant`);

  const typeOptions = useTypeOptions(
    (state) => state.scout.assistantsData?.[mapScoutTabsToAssistantCategories[scoutTab]],
  );

  const isFormLoading = useSelector((state) => state.scout.isFormLoading);
  const isResetting = useSelector((state) => state.scout.isResetting);
  const isGenerating = useSelector((state) => state.scout.isGenerating);
  const isFileUploading = useSelector((state) => state.scout.isFileUploading);

  const disableTextFields =
    isFormLoading ||
    isResetting ||
    isGenerating ||
    scoutTab === SCOUT_TABS.SCREENED_MESSAGES ||
    scoutTab === SCOUT_TABS.BACKUP;

  const disableDocButton =
    isFormLoading ||
    isResetting ||
    isFileUploading ||
    assistantInput.value?.type !== ASSISTANT_TYPES.FILE ||
    isGenerating;

  const handleAssistantClick = (newAssistant) => {
    const assistantData = typeOptions.find(({ value }) => value === newAssistant);

    form.change(`channels.${assistantCategory}.assistant`, assistantData);
    form.change('fileText', '');
  };

  const handleChangeTargetNumber = (newValue) => {
    dispatch(changeTargetNumber(newValue));
  };

  const toggleAdditionalInputs = () => {
    setIsAdditionalInputsOpen((prev) => !prev);
  };

  return (
    <form className={scss.form} id='generation_form' onSubmit={onSubmit}>
      <div className={scss.topBlock}>
        <div className={scss.fieldsSection}>
          <div className={scss.fieldsRow}>
            <Field name={`channels.${assistantCategory}.assistant`}>
              {({ meta, input }) => (
                <WithLabel variant={LABEL_STYLES.ROW} label={t('type')} isBold extraClassName={scss.typeField}>
                  <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                    <DropdownWithIcons
                      options={typeOptions}
                      value={input.value?.value}
                      onSelect={handleAssistantClick}
                      onBlur={input.onBlur}
                      onFocus={input.onFocus}
                      name={input.name}
                      placeholder={t('select')}
                      isError={meta.touched && meta.error}
                      disabled={disableTextFields}
                    />
                  </WithError>
                </WithLabel>
              )}
            </Field>

            <DocsButton
              selectedTypeOption={assistantInput.value}
              onFileUpload={onFileUpload}
              disabled={disableDocButton}
              isLoading={isFileUploading}
            />

            <Field name='url'>
              {({ meta, input }) => (
                <WithLabel variant={LABEL_STYLES.ROW} label='URL' isBold>
                  <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                    <Input
                      size='sm'
                      name={input.name}
                      value={input.value}
                      onChange={input.onChange}
                      onFocus={input.onFocus}
                      onBlur={input.onBlur}
                      disabled={disableTextFields}
                      isError={meta.touched && meta.error}
                    />
                  </WithError>
                </WithLabel>
              )}
            </Field>

            {scoutTab === SCOUT_TABS.FINAL_MESSAGES ? (
              <WithLabel variant={LABEL_STYLES.ROW} label={t('targetNumber')} isBold extraClassName={scss.fieldMedium}>
                <CommonDropdown
                  options={targetNumberOptions}
                  value={targetNumber}
                  onSelect={handleChangeTargetNumber}
                />
              </WithLabel>
            ) : null}

            <div
              className={scss.additionalInputs}
              onClick={toggleAdditionalInputs}
              role='presentation'
              onKeyDown={toggleAdditionalInputs}>
              <Icon
                icon={ICONS.CaretUp}
                className={clsx(scss.collapseIcon, !isAdditionalInputsOpen && scss.iconDown)}
              />
              <div className={scss.additionalInputsTitle}>{t('additionalInputs')}</div>
            </div>
          </div>

          <div className={scss.fieldsRow}>
            <AdditionalFields disableTextFields={disableTextFields} isOpen={isAdditionalInputsOpen} />
          </div>
        </div>

        <div className={scss.buttonsSection}>
          <GenerateButton onSubmit={onSubmit} onResetSession={onResetSession} />
        </div>
      </div>

      {scoutTab !== SCOUT_TABS.FINAL_MESSAGES ? <RepliesForm onReply={onReply} /> : null}
      {scoutTab === SCOUT_TABS.FINAL_MESSAGES ? <VariationScore /> : null}
    </form>
  );
};

export default memo(GenerationForm);
